import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faHistory } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminStaff } from "../store/admin";
import {
  formatDate,
  staffName,
  staffAvatar,
  staffIsActive,
  statusBadgeClass,
} from "./components/staff/staffUtils";

// ===== STAFF VIEW PAGE — GET /api/admin/staff/:id =====
// Shows only the staff member's profile. Activity lives on a separate page at
// /admin/staff-management/:staffId/activity.
const StaffDetailPage = () => {
  const { staffId } = useParams();
  const navigate = useNavigate();

  const {
    staffDetail,
    staffDetailLoading,
    staffDetailError,
    fetchStaffMember,
  } = useAdminStaff();

  const [isLoaded, setIsLoaded] = useState(false);

  // GET /api/admin/staff/:id
  useEffect(() => {
    if (staffId) {
      fetchStaffMember(staffId).finally(() => setIsLoaded(true));
    }
  }, [staffId, fetchStaffMember]);

  const goBack = () => navigate("/admin/staff-management");
  const goToActivity = () => navigate(`/admin/staff-management/${staffId}/activity`);

  const detailRows = staffDetail
    ? [
        ["Name", staffName(staffDetail)],
        ["Email", staffDetail.email || "--"],
        ["Phone", staffDetail.phone || "--"],
        ["Status", staffDetail.status || (staffIsActive(staffDetail) ? "ACTIVE" : "INACTIVE")],
        ["Date Added", formatDate(staffDetail.createdAt)],
        ["Last Updated", formatDate(staffDetail.updatedAt)],
      ]
    : [];

  const avatar = staffAvatar(staffDetail);

  return (
    <div>
      <AdminNavBar
        title="Staff Management"
        subTitle={staffDetail ? staffName(staffDetail) : "Details"}
        context="View a staff member's profile."
      />
      <AdminPageBody>
        <div className="mb-3">
          <button
            onClick={goBack}
            className="mb-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Staff Management
          </button>
          <h4 className="mt-2 text-lg font-bold text-slate-900">
            {staffDetail ? staffName(staffDetail) : "Staff"}
          </h4>
          <span className="text-[13px] text-slate-500">{staffDetail?.email || ""}</span>
        </div>

        {/* HEADER: AVATAR + ACTION */}
        <AdminSectionHeader
          title="Profile"
          description="Account details for this staff member."
          actions={
            <button type="button" onClick={goToActivity} className={adminPrimaryButton}>
              <FontAwesomeIcon icon={faHistory} /> View Activity
            </button>
          }
        />

        <AdminTablePanel className="mt-4">
          {staffDetailLoading || (!isLoaded && !staffDetailError) ? (
            <AdminLoadingState />
          ) : staffDetailError ? (
            <div className="p-4 text-sm text-red-600">{staffDetailError}</div>
          ) : (
            <>
              <div className="flex items-center gap-4 border-b border-slate-200 px-4 py-4">
                {avatar ? (
                  <img
                    src={avatar}
                    alt="Profile"
                    className="h-16 w-16 rounded-full border border-slate-200 object-cover"
                  />
                ) : (
                  <img
                    src="/images/default_profile.png"
                    alt="Profile"
                    className="h-16 w-16 rounded-full border border-slate-200 object-cover"
                  />
                )}
                <div>
                  <div className="text-base font-semibold text-slate-900">
                    {staffDetail ? staffName(staffDetail) : "--"}
                  </div>
                  <div className="text-[13px] text-slate-500">{staffDetail?.email || "--"}</div>
                </div>
              </div>
              <table className="admin-table-responsive w-full border-collapse text-[13px]">
                <tbody>
                  {detailRows.map(([label, value]) => (
                    <tr key={label} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="w-56 px-3 py-2.5 font-medium text-slate-600">{label}</td>
                      <td className="px-3 py-2.5 text-slate-900">
                        {label === "Status" ? (
                          <span className={statusBadgeClass(value)}>{value}</span>
                        ) : (
                          value
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </AdminTablePanel>
      </AdminPageBody>
    </div>
  );
};

export default StaffDetailPage;
